import {
  booleanAttribute,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  Input,
  Renderer2,
  output,
} from '@angular/core';

import { trackByProp } from '@recruitee/common';
import { I18nService, TranslatePipe } from '@recruitee/i18n';
import type { Placement } from '@recruitee/placements-types';
import type { Stage } from '@recruitee/stages-types';
import { StageGroup } from '@recruitee/stages-types';
import type { ConnectedOverlayDirective } from '@recruitee/user-interface';
import {
  DropdownComponent,
  DropdownHeaderComponent,
  ScrollableDirective,
} from '@recruitee/user-interface';

import { StageItemComponent } from './stage-item/stage-item.component';
import type { StageWithProgress } from './types';

@Component({
  selector: 'rt-candidate-stage-menu',
  styleUrls: ['./candidate-stage.menu.component.less'],
  templateUrl: './candidate-stage.menu.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    DropdownComponent,
    DropdownHeaderComponent,
    ScrollableDirective,
    StageItemComponent,
    TranslatePipe,
  ],
})
export class CandidateStageMenuComponent {
  public readonly stageGroupTranslations: Record<StageGroup, string> = {
    [StageGroup.Applicants]: this.i18n.translate('app.stages.divider.applicants_stages'),
    [StageGroup.Active]: this.i18n.translate('app.stages.divider.active_process_stages'),
    [StageGroup.Hires]: this.i18n.translate('app.stages.divider.hires_stages'),
    [StageGroup.Other]: this.i18n.translate('app.stages.divider.other_stages'),
  };

  @Input() public dropdownRef: ConnectedOverlayDirective;
  @Input() public placement: Placement;
  @Input() public stagesWithProgress: StageWithProgress[];
  @Input({ transform: booleanAttribute }) public canProceed = true;

  public stageClick = output<{
    placement: Placement;
    stage: Stage;
  }>();
  public closeClick = output<void>();
  public showDetailsClick = output<Partial<Stage>>();

  public trackById = trackByProp<Stage>({ prop: 'id' });

  constructor(
    private elRef: ElementRef,
    private renderer: Renderer2,
    private i18n: I18nService,
  ) {}

  public ngOnInit(): void {
    const { top } = this.dropdownRef.overlayRef.overlayElement.getBoundingClientRect();
    this.renderer.setStyle(
      this.elRef.nativeElement.querySelector('.stages-dropdown'),
      'maxHeight',
      `calc(95vh - ${top}px)`,
    );
  }

  protected onStageClick(placement: Placement, stage: StageWithProgress): void {
    if (stage.progress === 'current') {
      return;
    }

    this.canProceed && this.stageClick.emit({ placement, stage });
  }
}
